import api from '../service/api.service';
import {
  PaginationQueryParams,
  ResultSet,
  VendorAccount,
  VendorAccountShort,
  VendorAccountDetail,
  VendorAchAccount,
  VendorCheckAccount,
  VendorFileUploadHistory,
  VendorDocumentData,
  FilterTypes,
  VendorCommentsResponse,
  PaymentHistoryData,
  PaymentHistoryResultSet,
} from '../../types';
import { parseQuerySortFields } from '../../helpers/parseQuerySortFields';
import { parseVendorQueryFilters } from '../../helpers/parseQueryFilters';
import { BankingFormData, BankingInfo } from '../../views/VendorsView/types';
const BASE_URL = 'v1/fo/vendors';
const accountingSystem = localStorage.getItem('accountingSystem');

export interface ActiveVendorAccountOption {
  id: number;
  name: string;
  defaultVendorPaymentMethod: 'ACH' | 'CHECK' | 'V' | '';
  activePaymentMethod: string[];
  activeBankAccounts?: Array<{
    id: number;
    isDefault: boolean;
    provisionStatus: string;
  }>;
  activeMailingAddresses?: Array<{
    id: number;
    isDefault: boolean;
    provisionStatus: string;
  }>;
  achAccounts?: VendorAchAccount[];
  checkAccounts?: VendorCheckAccount[];
  accounting?: { isSynced?: boolean; externalVendorId?: string | null };
}

export interface VendorPaymentMethodDetails {
  vendorId: number;
  defaultVendorPaymentMethod: 'ACH' | 'CHECK' | 'V' | '';
  activePaymentMethod: string[];
  achAccounts: VendorAchAccount[];
  checkAccounts: VendorCheckAccount[];
}

export interface BankRoutingLookupResponse {
  bankName?: string;
  routingNumber?: string;
}


export const getVendorAccounts = async ({
  size,
  page,
  count = false,
  match = '',
  filters,
  activeSortField,
}: PaginationQueryParams) => {
  const queryFilters = parseVendorQueryFilters(filters);
  const sortFilters = parseQuerySortFields(activeSortField);

  const targetUrl = `${BASE_URL}?size=${size}&page=${page}&match=${match}&${queryFilters}${sortFilters}&count=${count}`;

  return await api.get<ResultSet<VendorAccountShort>>(targetUrl);
};

export const updateVendorAccount = async (
  accountId: number,
  data: {
    name?: string;
    tin?: string | null;
    defaultPaymentMethod?: 'ACH' | 'CHECK' | 'V';
    achAccounts?: Array<{
      id?: number | string;
      nickname: string;
      accountType: string;
      routingNumber: string;
      accountNumber?: string;
      maskedAccountNumber?: string;
      isDefault: boolean;
    }>;
    checkAccounts?: Array<{
      id?: number | string;
      nickname: string;
      payeeName?: string;
      recipientName?: string;
      address: string;
      addressLine2?: string;
      city: string;
      state: string;
      zip: string;
      country?: string;
      isDefault: boolean;
    }>;
  },
) => {
  const targetUrl = `${BASE_URL}/${accountId}/edit`;

  return await api.put<VendorAccount>(targetUrl, data);
};

export const createVendorAccount = async (
  data: {
    name: string;
    tin?: string | null;
    participationAddress1: string;
    participationCity: string;
    participationState: string;
    participationZip: string;
    email: string;
    vendorType: string;
    accountType: string;
    accountNumber: string;
    routingNumber: string;
    mailingAddress1: string;
    mailingAddress2: string;
    mailingCity: string;
    mailingState: string;
    mailingZip: string;
    mailingPhoneNumber: string;
    isVendorConfirmationRequired: boolean;
    contactName: string;
    isAddressVerified?: boolean;
    isMailingAddressVerified?: boolean;
    participationAddress2?: string;
    defaultPaymentMethod?: 'ACH' | 'CHECK' | 'V';
    notes?: string;
    achAccounts?: Array<{
      id?: number | string;
      nickname: string;
      accountType: string;
      routingNumber: string;
      accountNumber?: string;
      maskedAccountNumber?: string;
      isDefault: boolean;
    }>;
    checkAccounts?: Array<{
      id?: number | string;
      nickname: string;
      payeeName?: string;
      recipientName?: string;
      address: string;
      addressLine2?: string;
      city: string;
      state: string;
      zip: string;
      country?: string;
      isDefault: boolean;
    }>;
  },
  files?: File[]
) => {
  const targetUrl = `${BASE_URL}/create-vendor-account`;

  const buildFormData = (payload: typeof data) => {
    const formData = new FormData();

    const appendObjectArray = (
      key: 'achAccounts' | 'checkAccounts',
      items: NonNullable<typeof payload[typeof key]>,
    ) => {
      items.forEach((item, index) => {
        Object.entries(item).forEach(([nestedKey, nestedValue]) => {
          if (nestedValue !== undefined && nestedValue !== null) {
            formData.append(`${key}[${index}][${nestedKey}]`, String(nestedValue));
          }
        });
      });
    };

    (Object.keys(payload) as Array<keyof typeof payload>).forEach((key) => {
      const value = payload[key];
      if (key === 'achAccounts' || key === 'checkAccounts') {
        if (Array.isArray(value) && value.length > 0) {
          appendObjectArray(key, value as NonNullable<typeof payload[typeof key]>);
        }
        return;
      }

      if (value !== undefined && value !== null) {
        if (Array.isArray(value) || typeof value === 'object') {
          formData.append(key, JSON.stringify(value));
        } else {
          formData.append(key, String(value));
        }
      }
    });

    if (files && files.length > 0) {
      files.forEach((file) => formData.append('files', file));
    }

    return formData;
  };

  return await api.post<VendorAccount>(targetUrl, buildFormData(data));
};

export const getVendorAccount = async (accountId: number) => {
  const targetUrl = `${BASE_URL}/${accountId}`;
  return await api.get<VendorAccountDetail>(targetUrl);
};

export const getVendorPaymentMethodDetails = async (accountId: number) => {
  const targetUrl = `${BASE_URL}/${accountId}/payment-method-details`;
  return await api.get<VendorPaymentMethodDetails>(targetUrl);
};

export const getBankRoutingDetails = async (
  routingNumber: string,
): Promise<BankRoutingLookupResponse> => {
  const response = await api.get<BankRoutingLookupResponse>(
    `v1/bank-routing/${encodeURIComponent(routingNumber)}`,
  );

  return response.data;
};

export const addACHAccount = async (
  accountId: number,
  data: {
    nickname: string;
    accountType: string;
    routingNumber: string;
    accountNumber?: string;
    maskedAccountNumber?: string;
    isDefault: boolean;
  },
) => {
  const targetUrl = `${BASE_URL}/${accountId}/ach-accounts`;
  return await api.post<VendorAchAccount>(targetUrl, data);
};

export const editACHAccount = async (
  accountId: number,
  achAccountId: number | string,
  data: {
    nickname: string;
    accountType: string;
    routingNumber: string;
    accountNumber?: string;
    maskedAccountNumber?: string;
    isDefault: boolean;
  },
) => {
  const targetUrl = `${BASE_URL}/${accountId}/ach-accounts/${achAccountId}`;
  return await api.put<VendorAchAccount>(targetUrl, data);
};

export const deleteACHAccount = async (accountId: number, achAccountId: number | string) => {
  const targetUrl = `${BASE_URL}/${accountId}/ach-accounts/${achAccountId}`;
  return await api.delete<{ message?: string; success?: boolean }>(targetUrl);
};

export const editCheckAccount = async (
  accountId: number,
  checkAccountId: number | string,
  data: {
    nickname: string;
    payeeName?: string;
    recipientName?: string;
    address: string;
    addressLine2?: string;
    city: string;
    state: string;
    zip: string;
    country?: string;
    isDefault: boolean;
  },
) => {
  const targetUrl = `${BASE_URL}/${accountId}/check-accounts/${checkAccountId}`;
  return await api.put<VendorCheckAccount>(targetUrl, data);
};

export const addCheckAccount = async (
  accountId: number,
  data: {
    nickname: string;
    payeeName?: string;
    recipientName?: string;
    address: string;
    addressLine2?: string;
    city: string;
    state: string;
    zip: string;
    country?: string;
    isDefault: boolean;
    mailingPhoneNumber: string;
  },
) => {
  const targetUrl = `${BASE_URL}/${accountId}/check-accounts`;
  return await api.post<VendorCheckAccount>(targetUrl, data);
};

export const deleteCheckAccount = async (
  accountId: number,
  checkAccountId: number | string,
) => {
  const targetUrl = `${BASE_URL}/${accountId}/check-accounts/${checkAccountId}`;
  return await api.delete<{ message?: string; success?: boolean }>(targetUrl);
};

export const getActiveVendorAccounts = async (match?: string, filters?: FilterTypes) => {
  const queryFilters = parseVendorQueryFilters(filters);
  const matchQuery = match ? `match=${match}` : '';
  const separator = matchQuery && queryFilters ? '&' : '';
  const targetUrl = `${BASE_URL}/active-vendors${matchQuery || queryFilters ? '?' : ''}${matchQuery}${separator}${queryFilters}`;

  return await api.get<ActiveVendorAccountOption[]>(targetUrl);
};

export const findVendorAccount = async (searchQuery?: string) => {
  const targetUrl = `${BASE_URL}/search${searchQuery ? `?search=${searchQuery}` : ''}`;
  return await api.get<{
    vendors: Array<{
      id: number;
      tin: string | null;
      name: string;
      address1: string;
      address2: string;
      city: string;
      countryState: string;
      zip: string;
      createdAt: string;
      updatedAt: string;
    }>;
    count: number;
    hasMore: boolean;
  }>(targetUrl);
};

export const resendConfirmationEmail = async (accountId: number) => {
  const targetUrl = `${BASE_URL}/${accountId}/resend-confirmation-email`;
  return await api.post<{ message: string, success: boolean }>(targetUrl);
}

export const getAchAccountNumber = async (id: number) => {
  const targetUrl = `${BASE_URL}/${id}/account-number`; //
  return await api.get<{ accountNumber: string }>(targetUrl);
};

export const getOriginalAccountNumberFromHistory = async (participationId: number) => {
  const targetUrl = `${BASE_URL}/${participationId}/original-account-number-from-history`;
  return await api.get<{ accountNumber: string }>(targetUrl);
};

export const getAchAccountNumberVendor = async (token: string) => {
  const targetUrl = `${BASE_URL}/confirmation/${token}/account-number`; //
  return await api.get<{ accountNumber: string }>(targetUrl);
};

export const retryProvisioning = async (accountId: number) => {
  const targetUrl = `${BASE_URL}/${accountId}/retry-provisioning`;
  return await api.post<{ message: string; success: boolean }>(targetUrl);
}

export const getBankingDetails = async (token: string) => {
  const targetUrl = `${BASE_URL}/confirmation/${token}`;
  return await api.get<BankingInfo>(targetUrl);
}

export const confirmBankingDetails = async (token: string) => {
  const targetUrl = `${BASE_URL}/confirmation/${token}`;
  return await api.post<{ message: string; success: boolean }>(targetUrl);
}

export const getVendorConsentDocument = async (token: string, documentKey: string) => {
  const targetUrl = `${BASE_URL}/confirmation/${token}/document-url/${documentKey}`;
  return await api.get(targetUrl);
};

export const updateBankingDetails = async (token: string, data: Partial<BankingFormData>) => {
  const {
    phoneNumber,
    mailingZipCode,
    isAddressVerified,
    isparticipationAddressVerified,
    ...rest
  } = data;

  const payload = {
    ...rest,
    mailingPhoneNumber: phoneNumber,
    mailingZip: mailingZipCode,
    isMailingAddressVerified: isAddressVerified,
    isAddressVerified: isparticipationAddressVerified,
  };
  const targetUrl = `${BASE_URL}/confirmation/${token}`;
  return await api.put<{ message: string; success: boolean }>(targetUrl, payload)
}

export const adminConfirmationEmail = async (accountId: number) => {
  const targetUrl = `${BASE_URL}/${accountId}/admin-confirm`;
  return await api.post<{ message: string, success: boolean }>(targetUrl);
}

export const getVendorFBO = async (participationId: number) => {
  const targetUrl = `${BASE_URL}/${participationId}/banking-info`;
  return await api.get(targetUrl);
};

export const vendorBulkUploadDocument = async (file: File) => {
  const targetUrl = `${BASE_URL}/bulk-upload`;

  const formData = new FormData();
  formData.append('file', file);

  return await api.post<{ message: string; success: boolean }>(targetUrl, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

export const getVendorFileUploadHistory = async ({
  size,
  page,
  count = false,
}: {
  size: number;
  page: number;
  count?: boolean;
}) => {
  const targetUrl = `${BASE_URL}/bulk-uploads?size=${size}&page=${page}&count=${count}`;
  return await api.get<ResultSet<VendorFileUploadHistory>>(targetUrl);
};

export const getVendorErrorDocument = async (documentKey: string) => {
  const targetUrl = `${BASE_URL}/${documentKey}/error-document-url`;
  return await api.get(targetUrl);
};

export const getVendorBulkUploadTemplateUrl = async () => {
  const targetUrl = `${BASE_URL}/bulk-upload-template-url`;
  return await api.get<{ downloadUrl: string }>(targetUrl);
};

export const setExternalVendorId = async (participationId: number, externalVendorId: string) => {
  const targetUrl = `${BASE_URL}/${participationId}/${accountingSystem}/accounting-party-id`;
  return await api.put<{ message: string; success: boolean }>(targetUrl, { externalVendorId });
};

export const removeExternalVendorId = async (participationId: number) => {
  const targetUrl = `${BASE_URL}/${participationId}/${accountingSystem}/accounting-party-id`;
  return await api.delete<{ message: string; success: boolean }>(targetUrl);
};


export const getVendorDocuments = async (
  participationId: number,
  params: { page: number; size: number; count?: boolean } = { page: 1, size: 500, count: true }
) => {
  const { page, size, count = true } = params;
  const targetUrl = `${BASE_URL}/${participationId}/documents?page=${page}&size=${size}&count=${count}`;
  return await api.get<{ resultSet: VendorDocumentData[]; totalCount: number | null }>(targetUrl);
};

export const uploadVendorDocuments = async (participationId: number, files: File[]) => {
  const formData = new FormData();
  files.forEach((file) => formData.append('files', file));
  const targetUrl = `${BASE_URL}/${participationId}/documents`;
  return await api.post<{ message: string }>(targetUrl, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export const deleteVendorDocument = async (participationId: number, docId: number) => {
  const targetUrl = `${BASE_URL}/${participationId}/documents/${docId}`;
  return await api.delete<void>(targetUrl);
};

export const downloadVendorDocument = async (participationId: number, docId: number) => {
  const targetUrl = `${BASE_URL}/${participationId}/documents/${docId}/download`;
  return await api.get<{ downloadUrl: string }>(targetUrl);
};

export const setVendorExternalVendorId = async (participationId: number, externalVendorId: string) => {
  const targetUrl = `${BASE_URL}/${participationId}/${accountingSystem}/accounting-party-id`;
  return await api.put<{
    isSynced: boolean;
    status: string;
    externalVendorId: string | null;
    lastSyncedAt: string | null;
    link: string | null;
  }>(targetUrl, { externalVendorId });
};

export const getVendorConfig = async () => {
  const targetUrl = `${BASE_URL}/vendor-config`;
  return await api.get<{ isVendorVisibilityRestricted: boolean, findVendorSource: string }>(targetUrl);
};

export const getVendorComments = async (participationId: number) => {
  const targetUrl = `${BASE_URL}/${participationId}/comments`;
  return await api.get<VendorCommentsResponse>(targetUrl);
};


export const getVendorPaymentHistory = async (participationId: number, {
  size,
  page,
  count = false,
  match = '',
  activeSortField,
}: PaginationQueryParams) => {
  const sortFilters = parseQuerySortFields(activeSortField);

  const targetUrl = `${BASE_URL}/${participationId}/payment-history?size=${size}&page=${page}&match=${match}&count=${count}${sortFilters}`;

  return await api.get<PaymentHistoryResultSet<PaymentHistoryData>>(targetUrl);
};

